/**
 * Named curve diagrams – the ones a subject draws over and over.
 *
 * curve-diagram.js knows how to draw axes, curves, crossings and shaded areas
 * and nothing about what any of it means. This is where the meaning goes: a
 * preset takes a few options ("demand shifts right") and builds the general
 * spec for it, with the curves placed so they cross where they should and the
 * labels a teacher of that subject would expect.
 *
 * Presets are tagged with a subject but are otherwise just entries in a
 * registry: economics, physics, chemistry, biology, maths, geography and
 * business so far. Another is one more register() call and touches nothing
 * else. Anything without a preset is still drawable – `custom` passes a
 * hand-written spec straight through.
 *
 * A preset never reaches the renderer or the stored document. resolve() turns
 * `{ preset, options, labels }` into a plain spec, and that is what is saved,
 * edited and drawn.
 *
 * UMD, no DOM, same as curve-diagram.js.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory(require('./curve-diagram.js'));
    else root.CurveDiagramPresets = factory(root.CurveDiagram);
}(typeof self !== 'undefined' ? self : this, function (CurveDiagram) {
    'use strict';

    const REGISTRY = new Map();

    /**
     * @param {object} preset { id, subject, title, description, options, labels, build }
     *   options: { name: { values: [...], default, description } } – every option is a closed choice
     *   labels:  ids whose text a caller may override (curve ids, point ids, 'x', 'y')
     *   build(options) -> a curve-diagram spec
     */
    function register(preset) {
        REGISTRY.set(preset.id, preset);
    }

    const line = (x1, y1, x2, y2) => [{ x: x1, y: y1 }, { x: x2, y: y2 }];
    const shiftX = (pts, dx) => pts.map(p => ({ x: p.x + dx, y: p.y }));
    const shiftY = (pts, dy) => pts.map(p => ({ x: p.x, y: p.y + dy }));
    const round = (n) => Math.round(n * 100) / 100;
    const fnCurve = (fn, from, to, n = 28) => {
        const out = [];
        for (let i = 0; i <= n; i++) { const x = from + ((to - from) * i) / n; out.push({ x: round(x), y: round(fn(x)) }); }
        return out;
    };
    const SIGN = { right: 1, left: -1, up: 1, down: -1, outward: 1, inward: -1 };
    const SHIFT = { values: ['none', 'right', 'left'], default: 'none' };

    // The two straight lines almost every market diagram starts from.
    const DEMAND = line(10, 85, 85, 15);
    const SUPPLY = line(10, 15, 85, 85);
    // The same two lines carried back to the price axis.
    const DEMAND_FULL = line(0, round(85 + 10 * 70 / 75), 85, 15);
    const SUPPLY_FULL = line(0, round(15 - 10 * 70 / 75), 85, 85);

    // ---- Economics ---------------------------------------------------------

    register({
        id: 'supply-demand',
        subject: 'economics',
        title: 'Supply and demand',
        description: 'A market in equilibrium, with an optional shift of demand and/or supply to a new equilibrium, or consumer/producer surplus shaded. Also any other market drawn the same way (labour: relabel y "Wage rate", x "Quantity of labour"; currency: y "Exchange rate"; loanable funds: y "Interest rate").',
        options: {
            demandShift: { ...SHIFT, description: 'demand curve shifts right (increase) or left (decrease)' },
            supplyShift: { ...SHIFT, description: 'supply curve shifts right (increase) or left (decrease)' },
            shade: { values: ['none', 'consumer-surplus', 'producer-surplus', 'both-surplus'], default: 'none', description: 'area to shade at the original equilibrium' },
        },
        labels: ['x', 'y', 'D1', 'S1', 'D2', 'S2', 'E1', 'E2'],
        build(o) {
            const dShift = o.demandShift !== 'none', sShift = o.supplyShift !== 'none';
            const moved = dShift || sShift;
            // A surplus is the area out to the price axis, so those curves run all the way to it.
            const toAxis = !moved && o.shade !== 'none';
            const curves = [
                { id: 'D1', label: moved && dShift ? 'D1' : 'D', points: toAxis ? DEMAND_FULL : DEMAND, tone: 'a' },
                { id: 'S1', label: moved && sShift ? 'S1' : 'S', points: toAxis ? SUPPLY_FULL : SUPPLY, tone: 'b' },
            ];
            const arrows = [];
            if (dShift) {
                curves.push({ id: 'D2', label: 'D2', points: shiftX(DEMAND, 18 * SIGN[o.demandShift]), tone: 'a', step: 2 });
                arrows.push({ from: 'D1', to: 'D2', step: 2 });
            }
            if (sShift) {
                curves.push({ id: 'S2', label: 'S2', points: shiftX(SUPPLY, 18 * SIGN[o.supplyShift]), tone: 'b', step: 2 });
                arrows.push({ from: 'S1', to: 'S2', step: 2 });
            }
            const points = [{ id: 'E1', intersect: ['D1', 'S1'], guides: 'both', xLabel: moved ? 'Q1' : 'Q', yLabel: moved ? 'P1' : 'P', label: moved ? '' : 'E' }];
            if (moved) points.push({ id: 'E2', intersect: [dShift ? 'D2' : 'D1', sShift ? 'S2' : 'S1'], guides: 'both', xLabel: 'Q2', yLabel: 'P2', step: 3 });
            const regions = [];
            if (!moved && (o.shade === 'consumer-surplus' || o.shade === 'both-surplus')) {
                regions.push({ label: 'Consumer surplus', tone: 'a', vertices: [DEMAND_FULL[0], 'E1', { x: 0, yOf: 'E1' }], step: 2 });
            }
            if (!moved && (o.shade === 'producer-surplus' || o.shade === 'both-surplus')) {
                regions.push({ label: 'Producer surplus', tone: 'b', vertices: [SUPPLY_FULL[0], 'E1', { x: 0, yOf: 'E1' }], step: 2 });
            }
            return { title: 'Supply and demand diagram', axes: { origin: 'O', x: { label: 'Quantity' }, y: { label: 'Price' } }, curves, points, regions, arrows };
        },
    });

    register({
        id: 'price-control',
        subject: 'economics',
        title: 'Maximum or minimum price',
        description: 'A price ceiling below equilibrium (shortage / excess demand) or a price floor above it (surplus / excess supply). Also a minimum wage: type "floor", relabel y "Wage rate", x "Quantity of labour".',
        options: {
            type: { values: ['ceiling', 'floor'], default: 'ceiling', description: 'ceiling = maximum price, floor = minimum price' },
        },
        labels: ['x', 'y', 'D', 'S', 'control', 'gap', 'E'],
        build(o) {
            const ceiling = o.type === 'ceiling';
            const y = ceiling ? 32 : 68;
            return {
                title: ceiling ? 'Maximum price diagram' : 'Minimum price diagram',
                axes: { origin: 'O', x: { label: 'Quantity' }, y: { label: 'Price', marks: [{ at: y, label: ceiling ? 'P_{max}' : 'P_{min}' }] } },
                curves: [
                    { id: 'D', label: 'D', points: DEMAND, tone: 'a' },
                    { id: 'S', label: 'S', points: SUPPLY, tone: 'b' },
                    { id: 'control', label: ceiling ? 'Maximum price' : 'Minimum price', points: line(0, y, 92, y), tone: 'c', step: 2 },
                ],
                points: [
                    { id: 'E', intersect: ['D', 'S'], guides: 'both', xLabel: 'Q_e', yLabel: 'P_e' },
                    { id: 'Qs', on: { curve: 'S', y }, guides: 'x', xLabel: 'Q_s', step: 2 },
                    { id: 'Qd', on: { curve: 'D', y }, guides: 'x', xLabel: 'Q_d', step: 2 },
                ],
                arrows: [{ id: 'gap', from: { xOf: ceiling ? 'Qs' : 'Qd', y: y - 5 }, to: { xOf: ceiling ? 'Qd' : 'Qs', y: y - 5 }, heads: 'both', label: ceiling ? 'Shortage' : 'Surplus', step: 3 }],
            };
        },
    });

    register({
        id: 'indirect-tax',
        subject: 'economics',
        title: 'Indirect tax',
        description: 'A specific (per-unit) tax shifting supply vertically upward, with the incidence on consumers and producers, the tax revenue, or the deadweight loss shaded.',
        options: {
            shade: { values: ['incidence', 'revenue', 'deadweight-loss', 'none'], default: 'incidence', description: 'what to shade' },
        },
        labels: ['x', 'y', 'D', 'S1', 'S2'],
        build(o) {
            const points = [
                { id: 'E1', intersect: ['D', 'S1'], guides: 'both', xLabel: 'Q1', yLabel: 'P1' },
                { id: 'E2', intersect: ['D', 'S2'], guides: 'both', xLabel: 'Q2', yLabel: 'P2', step: 2 },
                // What the producer keeps: straight down from the new equilibrium to the old supply curve.
                { id: 'F', on: { curve: 'S1', xOf: 'E2' }, guides: 'y', yLabel: 'P3', step: 3 },
            ];
            const consumer = [{ x: 0, yOf: 'E2' }, 'E2', { xOf: 'E2', yOf: 'E1' }, { x: 0, yOf: 'E1' }];
            const producer = [{ x: 0, yOf: 'E1' }, { xOf: 'E2', yOf: 'E1' }, 'F', { x: 0, yOf: 'F' }];
            const regions = {
                incidence: [
                    { label: 'Consumer burden', tone: 'a', vertices: consumer, step: 3 },
                    { label: 'Producer burden', tone: 'b', vertices: producer, step: 3 },
                ],
                revenue: [{ label: 'Tax revenue', tone: 'c', vertices: [{ x: 0, yOf: 'E2' }, 'E2', 'F', { x: 0, yOf: 'F' }], step: 3 }],
                'deadweight-loss': [{ label: 'DWL', tone: 'd', vertices: ['E2', 'E1', 'F'], step: 3 }],
                none: [],
            }[o.shade];
            return {
                title: 'Indirect tax diagram',
                axes: { origin: 'O', x: { label: 'Quantity' }, y: { label: 'Price' } },
                curves: [
                    { id: 'D', label: 'D', points: DEMAND, tone: 'a' },
                    { id: 'S1', label: 'S1', points: SUPPLY, tone: 'b' },
                    { id: 'S2', label: 'S2 (S1 + tax)', points: shiftY(SUPPLY, 20), tone: 'b', step: 2 },
                ],
                points, regions,
                arrows: [{ from: 'S1', to: 'S2', along: 'y', step: 2 }],
            };
        },
    });

    register({
        id: 'subsidy',
        subject: 'economics',
        title: 'Subsidy',
        description: 'A per-unit subsidy to producers shifting supply vertically downward, with the total cost of the subsidy optionally shaded.',
        options: {
            shade: { values: ['cost', 'none'], default: 'cost', description: 'shade the total government spending on the subsidy' },
        },
        labels: ['x', 'y', 'D', 'S1', 'S2'],
        build(o) {
            return {
                title: 'Subsidy diagram',
                axes: { origin: 'O', x: { label: 'Quantity' }, y: { label: 'Price' } },
                curves: [
                    { id: 'D', label: 'D', points: DEMAND, tone: 'a' },
                    { id: 'S1', label: 'S1', points: SUPPLY, tone: 'b' },
                    { id: 'S2', label: 'S2 (S1 + subsidy)', points: shiftY(SUPPLY, -20), tone: 'b', step: 2 },
                ],
                points: [
                    { id: 'E1', intersect: ['D', 'S1'], guides: 'both', xLabel: 'Q1', yLabel: 'P1' },
                    { id: 'E2', intersect: ['D', 'S2'], guides: 'both', xLabel: 'Q2', yLabel: 'P2', step: 2 },
                    // What the producer receives per unit: straight up from the new equilibrium to the old supply curve.
                    { id: 'F', on: { curve: 'S1', xOf: 'E2' }, guides: 'y', yLabel: 'P3', step: 3 },
                ],
                regions: o.shade === 'cost'
                    ? [{ label: 'Cost of subsidy', tone: 'c', vertices: [{ x: 0, yOf: 'F' }, 'F', 'E2', { x: 0, yOf: 'E2' }], step: 3 }]
                    : [],
                arrows: [{ from: 'S1', to: 'S2', along: 'y', step: 2 }],
            };
        },
    });

    register({
        id: 'externality',
        subject: 'economics',
        title: 'Externality (MSC / MSB)',
        description: 'Marginal private and social cost and benefit, the free-market and socially optimal outputs, and the welfare loss triangle. Use for merit and demerit goods too (demerit = negative-consumption, merit = positive-consumption).',
        options: {
            type: { values: ['negative-production', 'positive-production', 'negative-consumption', 'positive-consumption'], default: 'negative-production', description: 'which kind of externality' },
        },
        labels: ['x', 'y', 'MPC', 'MSC', 'MPB', 'MSB', 'welfare'],
        build(o) {
            const production = o.type.endsWith('production'), negative = o.type.startsWith('negative');
            const gap = 20;
            let curves, privCost, socCost, privBen, socBen;
            if (production) {
                // Cost side splits; benefit is one curve doing both jobs.
                privCost = 'MPC'; socCost = 'MSC'; privBen = socBen = 'MPB';
                curves = [
                    { id: 'MPB', label: 'MPB = MSB', points: DEMAND, tone: 'a' },
                    { id: 'MPC', label: 'MPC', points: SUPPLY, tone: 'b' },
                    { id: 'MSC', label: 'MSC', points: shiftY(SUPPLY, negative ? gap : -gap), tone: 'd', step: 2 },
                ];
            } else {
                privCost = socCost = 'MPC'; privBen = 'MPB'; socBen = 'MSB';
                curves = [
                    { id: 'MPC', label: 'MPC = MSC', points: SUPPLY, tone: 'b' },
                    { id: 'MPB', label: 'MPB', points: DEMAND, tone: 'a' },
                    { id: 'MSB', label: 'MSB', points: shiftY(DEMAND, negative ? -gap : gap), tone: 'd', step: 2 },
                ];
            }
            return {
                title: `${negative ? 'Negative' : 'Positive'} ${production ? 'production' : 'consumption'} externality diagram`,
                axes: { origin: 'O', x: { label: 'Quantity' }, y: { label: 'Costs / benefits' } },
                curves,
                points: [
                    { id: 'M', intersect: [privCost, privBen], guides: 'both', xLabel: 'Q1', yLabel: 'P1' },
                    { id: 'Opt', intersect: [socCost, socBen], guides: 'both', xLabel: 'Q*', yLabel: 'P*', step: 2 },
                    // The welfare triangle's far side: social cost and social benefit at the market's output.
                    { id: 'A', on: { curve: socCost, xOf: 'M' }, dot: false, step: 3 },
                    { id: 'B', on: { curve: socBen, xOf: 'M' }, dot: false, step: 3 },
                ],
                regions: [{ id: 'welfare', label: 'Welfare loss', tone: 'd', vertices: ['Opt', 'A', 'B'], step: 3 }],
            };
        },
    });

    register({
        id: 'ad-as',
        subject: 'economics',
        title: 'Aggregate demand and aggregate supply',
        description: 'AD/AS: short-run only, classical (with a vertical LRAS), or a Keynesian AS curve; with optional shifts of AD, SRAS and LRAS.',
        options: {
            model: { values: ['short-run', 'classical', 'keynesian'], default: 'short-run', description: 'short-run = AD and SRAS; classical adds a vertical LRAS; keynesian = one AS curve that is flat, then rising, then vertical' },
            adShift: { ...SHIFT, description: 'AD shifts right (increase) or left (decrease)' },
            srasShift: { ...SHIFT, description: 'SRAS shifts right (increase, costs fall) or left (decrease, costs rise); ignored for keynesian' },
            lrasShift: { ...SHIFT, description: 'LRAS (or the Keynesian AS) shifts right (growth in capacity) or left; ignored for short-run' },
        },
        labels: ['x', 'y', 'AD1', 'AD2', 'SRAS1', 'SRAS2', 'LRAS1', 'LRAS2', 'AS1', 'AS2'],
        build(o) {
            const AD = line(15, 85, 85, 15), SRAS = line(15, 15, 85, 85);
            const keynesian = o.model === 'keynesian';
            const adMoved = o.adShift !== 'none';
            const srasMoved = !keynesian && o.srasShift !== 'none';
            const lrasMoved = o.model !== 'short-run' && o.lrasShift !== 'none';
            const curves = [{ id: 'AD1', label: adMoved ? 'AD1' : 'AD', points: AD, tone: 'a' }];
            const arrows = [];
            let supply1, supply2;
            if (keynesian) {
                const AS = [{ x: 4, y: 24 }, { x: 34, y: 24 }, { x: 52, y: 30 }, { x: 64, y: 44 }, { x: 71, y: 64 }, { x: 73, y: 94 }];
                supply1 = 'AS1';
                curves.push({ id: 'AS1', label: lrasMoved ? 'AS1' : 'AS', points: AS, smooth: true, tone: 'b' });
                if (lrasMoved) {
                    supply2 = 'AS2';
                    // Capacity grows: the flat stretch stays where it is and the vertical one moves.
                    const k = SIGN[o.lrasShift] * 14;
                    curves.push({ id: 'AS2', label: 'AS2', points: AS.map((p, i) => ({ x: p.x + (i === 0 ? 0 : k), y: p.y })), smooth: true, tone: 'b', step: 2 });
                    arrows.push({ from: 'AS1', to: 'AS2', step: 2 });
                }
            } else {
                supply1 = 'SRAS1';
                curves.push({ id: 'SRAS1', label: srasMoved ? 'SRAS1' : 'SRAS', points: SRAS, tone: 'b' });
                if (srasMoved) {
                    supply2 = 'SRAS2';
                    curves.push({ id: 'SRAS2', label: 'SRAS2', points: shiftX(SRAS, 18 * SIGN[o.srasShift]), tone: 'b', step: 2 });
                    arrows.push({ from: 'SRAS1', to: 'SRAS2', step: 2 });
                }
                if (o.model === 'classical') {
                    curves.push({ id: 'LRAS1', label: lrasMoved ? 'LRAS1' : 'LRAS', points: line(50, 4, 50, 94), tone: 'c' });
                    if (lrasMoved) {
                        const x = 50 + 18 * SIGN[o.lrasShift];
                        curves.push({ id: 'LRAS2', label: 'LRAS2', points: line(x, 4, x, 94), tone: 'c', step: 2 });
                        arrows.push({ from: 'LRAS1', to: 'LRAS2', step: 2 });
                    }
                }
            }
            if (adMoved) {
                curves.push({ id: 'AD2', label: 'AD2', points: shiftX(AD, 18 * SIGN[o.adShift]), tone: 'a', step: 2 });
                arrows.push({ from: 'AD1', to: 'AD2', step: 2 });
            }
            const moved = adMoved || !!supply2;
            const points = [{ id: 'E1', intersect: ['AD1', supply1], guides: 'both', xLabel: moved ? 'Y1' : 'Y', yLabel: moved ? 'PL1' : 'PL' }];
            if (moved) points.push({ id: 'E2', intersect: [adMoved ? 'AD2' : 'AD1', supply2 || supply1], guides: 'both', xLabel: 'Y2', yLabel: 'PL2', step: 3 });
            return { title: 'Aggregate demand and aggregate supply diagram', axes: { origin: 'O', x: { label: 'Real GDP' }, y: { label: 'Price level' } }, curves, points, arrows };
        },
    });

    register({
        id: 'ppf',
        subject: 'economics',
        title: 'Production possibility frontier',
        description: 'A PPF (production possibility curve), with points showing efficiency, inefficiency and the unattainable, a movement along the curve showing opportunity cost, or a shift of the whole frontier.',
        options: {
            show: { values: ['curve-only', 'efficiency', 'opportunity-cost'], default: 'efficiency', description: 'efficiency = points on, inside and outside the curve; opportunity-cost = two points on the curve with their quantities marked' },
            shift: { values: ['none', 'outward', 'inward'], default: 'none', description: 'outward = economic growth, inward = a fall in productive capacity' },
        },
        labels: ['x', 'y', 'PPF1', 'PPF2', 'A', 'B', 'C', 'D'],
        build(o) {
            // By angle, not by x: stepping along x leaves the last stretch, where
            // the curve drops to the axis, as one long straight line.
            const arc = (r) => {
                const out = [];
                for (let i = 0; i <= 24; i++) { const t = (Math.PI / 2) * (1 - i / 24); out.push({ x: round(r * Math.cos(t)), y: round(r * Math.sin(t)) }); }
                return out;
            };
            const R = 78, moved = o.shift !== 'none';
            const curves = [{ id: 'PPF1', label: moved ? 'PPF1' : 'PPF', points: arc(R), smooth: true, tone: 'a' }];
            const arrows = [];
            if (moved) {
                curves.push({ id: 'PPF2', label: 'PPF2', points: arc(R + 16 * SIGN[o.shift]), smooth: true, tone: 'a', step: 2 });
                arrows.push({ from: 'PPF1', to: 'PPF2', step: 2 });
            }
            const on = (deg) => ({ x: round(R * Math.cos(deg * Math.PI / 180)), y: round(R * Math.sin(deg * Math.PI / 180)) });
            let points = [];
            if (o.show === 'efficiency') {
                points = [
                    { id: 'A', label: 'A', at: on(62) },
                    { id: 'B', label: 'B', at: on(28) },
                    { id: 'C', label: 'C', at: { x: 32, y: 32 } },
                    // Outside the frontier it was drawn against, even after the frontier moves out.
                    { id: 'D', label: 'D', at: { x: 72, y: 72 } },
                ];
            } else if (o.show === 'opportunity-cost') {
                points = [
                    { id: 'A', label: 'A', at: on(62), guides: 'both', xLabel: 'X1', yLabel: 'Y1' },
                    { id: 'B', label: 'B', at: on(28), guides: 'both', xLabel: 'X2', yLabel: 'Y2', step: 2 },
                ];
            }
            return { title: 'Production possibility frontier', axes: { origin: 'O', x: { label: 'Consumer goods' }, y: { label: 'Capital goods' } }, curves, points, arrows };
        },
    });

    /**
     * Cost curves that are actually consistent with each other: both come from
     * one total cost function, TC = F + aQ - bQ² + cQ³, so MC cuts AC at AC's
     * lowest point because it has to, not because the points were placed there.
     * Fixed cost F moves AC without touching MC, which is how the three profit
     * cases below are told apart.
     */
    const COST = { a: 35, b: 0.6, c: 0.008 };
    const mc = (q) => COST.a - 2 * COST.b * q + 3 * COST.c * q * q;
    const avc = (q) => COST.a - COST.b * q + COST.c * q * q;
    const bisect = (fn, lo, hi) => {
        for (let i = 0; i < 60; i++) { const mid = (lo + hi) / 2; if (fn(lo) * fn(mid) <= 0) hi = mid; else lo = mid; }
        return (lo + hi) / 2;
    };

    register({
        id: 'firm-costs-revenue',
        subject: 'economics',
        title: 'Theory of the firm',
        description: 'A firm\'s MC and AC with its revenue curves, the profit-maximising output where MC = MR, and the profit or loss area. Monopolistic competition in the long run is "monopoly" with "normal" profit.',
        options: {
            structure: { values: ['monopoly', 'perfect-competition'], default: 'monopoly', description: 'monopoly = downward-sloping AR with MR below it (also monopolistic competition); perfect-competition = a horizontal AR = MR' },
            profit: { values: ['supernormal', 'normal', 'loss'], default: 'supernormal', description: 'the profit position at the profit-maximising output' },
        },
        labels: ['x', 'y', 'MC', 'AC', 'AR', 'MR', 'profit'],
        build(o) {
            const monopoly = o.structure === 'monopoly';
            const ar = (q) => 90 - 0.75 * q, mr = (q) => 90 - 1.5 * q;
            let F, curves, price;
            if (monopoly) {
                const q = bisect((x) => mr(x) - mc(x), 20, 70);
                // Zero profit at the best the firm can do means AR just touches AC there.
                const breakEven = (ar(q) - avc(q)) * q;
                F = breakEven * { supernormal: 0.62, normal: 1, loss: 1.3 }[o.profit];
                curves = [
                    { id: 'AR', label: 'AR = D', points: line(0, 90, 96, ar(96)), tone: 'a' },
                    { id: 'MR', label: 'MR', points: line(0, 90, 58, mr(58)), tone: 'a', style: 'dashed' },
                ];
            } else {
                // The firm takes the market price; fixed costs stay put and the price decides the profit.
                F = 850;
                const qMin = bisect((x) => mc(x) - (F / x + avc(x)), 30, 90);
                price = round(mc(qMin) + { supernormal: 17, normal: 0, loss: -9 }[o.profit]);
                curves = [{ id: 'AR', label: 'AR = MR = D', points: line(0, price, 92, price), tone: 'a' }];
            }
            const ac = (q) => F / q + avc(q);
            curves.push(
                { id: 'MC', label: 'MC', points: fnCurve(mc, 6, 76), smooth: true, tone: 'b' },
                { id: 'AC', label: 'AC', points: fnCurve(ac, 14, 88), smooth: true, tone: 'c' },
            );
            const points = [
                // MC = MR, on the rising stretch of MC.
                { id: 'M', intersect: ['MC', monopoly ? 'MR' : 'AR'], pick: 'last', guides: 'x', xLabel: 'Q1', dot: monopoly },
                { id: 'P', on: { curve: 'AR', xOf: 'M' }, guides: 'both', yLabel: 'P1', step: 2 },
            ];
            const regions = [];
            if (o.profit !== 'normal') {
                points.push({ id: 'C', on: { curve: 'AC', xOf: 'M' }, guides: 'y', yLabel: 'C1', step: 3 });
                regions.push({ id: 'profit', label: o.profit === 'loss' ? 'Loss' : 'Supernormal profit', tone: o.profit === 'loss' ? 'b' : 'c', vertices: [{ x: 0, yOf: 'P' }, 'P', 'C', { x: 0, yOf: 'C' }], step: 3 });
            }
            return {
                title: `${monopoly ? 'Monopoly' : 'Perfectly competitive firm'} diagram – ${o.profit === 'loss' ? 'loss' : `${o.profit} profit`}`,
                axes: { origin: 'O', x: { label: 'Output' }, y: { label: 'Costs / revenue' } },
                curves, points, regions,
            };
        },
    });

    register({
        id: 'tariff',
        subject: 'economics',
        title: 'Tariff',
        description: 'A tariff on imports: domestic supply and demand, the world price and the world price plus tariff, with tariff revenue and the two deadweight-loss triangles shaded.',
        options: {
            shade: { values: ['revenue-and-loss', 'revenue', 'none'], default: 'revenue-and-loss', description: 'what to shade' },
        },
        labels: ['x', 'y', 'D', 'S', 'Pw', 'Pt'],
        build(o) {
            const pw = 26, pt = 40;
            const regions = [];
            if (o.shade !== 'none') regions.push({ label: 'Tariff revenue', tone: 'c', vertices: ['S2', 'D2', { xOf: 'D2', y: pw }, { xOf: 'S2', y: pw }], step: 3 });
            if (o.shade === 'revenue-and-loss') {
                regions.push(
                    { label: '', tone: 'd', vertices: ['S1', 'S2', { xOf: 'S2', y: pw }], step: 3 },
                    { label: '', tone: 'd', vertices: ['D2', 'D1', { xOf: 'D2', y: pw }], step: 3 },
                );
            }
            return {
                title: 'Tariff diagram',
                axes: { origin: 'O', x: { label: 'Quantity' }, y: { label: 'Price', marks: [{ at: pw, label: 'P_w' }, { at: pt, label: 'P_w + t' }] } },
                curves: [
                    { id: 'D', label: 'Domestic D', points: DEMAND, tone: 'a' },
                    { id: 'S', label: 'Domestic S', points: SUPPLY, tone: 'b' },
                    { id: 'Pw', label: 'World S', points: line(0, pw, 94, pw), tone: 'ink' },
                    { id: 'Pt', label: 'World S + tariff', points: line(0, pt, 94, pt), tone: 'ink', style: 'dashed', step: 2 },
                ],
                points: [
                    { id: 'S1', on: { curve: 'S', y: pw }, guides: 'x', xLabel: 'Q1' },
                    { id: 'D1', on: { curve: 'D', y: pw }, guides: 'x', xLabel: 'Q4' },
                    { id: 'S2', on: { curve: 'S', y: pt }, guides: 'x', xLabel: 'Q2', step: 2 },
                    { id: 'D2', on: { curve: 'D', y: pt }, guides: 'x', xLabel: 'Q3', step: 2 },
                ],
                regions,
            };
        },
    });

    register({
        id: 'laffer-curve',
        subject: 'economics',
        title: 'Laffer curve',
        description: 'Tax revenue against the tax rate, with the revenue-maximising rate marked.',
        options: {},
        labels: ['x', 'y', 'T'],
        build() {
            return {
                title: 'Laffer curve',
                axes: { origin: 'O', x: { label: 'Tax rate (%)', marks: [{ at: 100, label: '100' }] }, y: { label: 'Tax revenue' } },
                curves: [{ id: 'L', label: '', points: fnCurve((x) => 80 * (1 - ((x - 50) / 50) ** 2), 0, 100, 30), smooth: true, tone: 'a' }],
                points: [{ id: 'T', at: { x: 50, y: 80 }, guides: 'both', xLabel: 'T*', yLabel: 'R_{max}' }],
            };
        },
    });

    register({
        id: 'phillips-curve',
        subject: 'economics',
        title: 'Phillips curve',
        description: 'The short-run trade-off between inflation and unemployment, optionally with the vertical long-run Phillips curve at the natural rate.',
        options: {
            longRun: { values: ['no', 'yes'], default: 'no', description: 'add the vertical long-run Phillips curve' },
        },
        labels: ['x', 'y', 'SRPC', 'LRPC'],
        build(o) {
            const srpc = (x) => 900 / (x + 4) - 6;
            const curves = [{ id: 'SRPC', label: o.longRun === 'yes' ? 'SRPC' : 'PC', points: fnCurve(srpc, 6, 92, 30), smooth: true, tone: 'a' }];
            const points = [];
            if (o.longRun === 'yes') {
                curves.push({ id: 'LRPC', label: 'LRPC', points: line(40, 2, 40, 94), tone: 'c', step: 2 });
                points.push({ id: 'N', intersect: ['SRPC', 'LRPC'], guides: 'none', xLabel: 'NRU', step: 2 });
            }
            return { title: 'Phillips curve', axes: { origin: 'O', x: { label: 'Unemployment (%)' }, y: { label: 'Inflation (%)' } }, curves, points };
        },
    });

    register({
        id: 'lorenz-curve',
        subject: 'economics',
        title: 'Lorenz curve',
        description: 'The distribution of income against the line of perfect equality, with the areas used for the Gini coefficient labelled A and B.',
        options: {},
        labels: ['x', 'y', 'equality', 'lorenz'],
        build() {
            return {
                title: 'Lorenz curve',
                axes: { origin: 'O', x: { label: 'Cumulative % of population', marks: [{ at: 100, label: '100' }] }, y: { label: 'Cumulative % of income', marks: [{ at: 100, label: '100' }] } },
                curves: [
                    // Both curves end in the same corner, so they are named beside themselves instead.
                    { id: 'equality', label: '', points: line(0, 0, 100, 100), tone: 'ink' },
                    { id: 'lorenz', label: '', points: fnCurve((x) => 100 * (x / 100) ** 2.6, 0, 100, 30), smooth: true, tone: 'a' },
                ],
                notes: [{ text: 'Line of equality', at: { x: 30, y: 54 } }, { text: 'Lorenz curve', at: { x: 54, y: 10 } }, { text: 'A', at: { x: 60, y: 43 } }, { text: 'B', at: { x: 87, y: 32 } }],
            };
        },
    });

    // ---- Shared by the science and maths presets ---------------------------

    const dashed = (id, pts) => ({ id, label: '', points: pts, style: 'dashed', tone: 'muted' });
    const note = (text, x, y, step) => ({ text, at: { x, y }, ...(step ? { step } : {}) });
    const quadrant = (x, y, extra = {}) => ({ origin: 'O', x: { label: x }, y: { label: y }, ...extra });

    // ---- Physics -----------------------------------------------------------

    register({
        id: 'distance-time',
        subject: 'physics',
        title: 'Distance-time graph',
        description: 'Distance against time for an object at constant speed, stationary, accelerating, or on a journey out, stopped and back.',
        options: {
            motion: { values: ['journey', 'constant-speed', 'stationary', 'accelerating'], default: 'journey', description: 'what the object is doing' },
        },
        labels: ['x', 'y'],
        build(o) {
            const built = {
                'constant-speed': { curves: [{ id: 'm', label: '', points: line(0, 0, 90, 80), tone: 'a' }], notes: [note('Constant speed', 64, 40)] },
                stationary: { curves: [{ id: 'm', label: '', points: line(0, 45, 92, 45), tone: 'a' }], notes: [note('Stationary', 46, 51)] },
                accelerating: { curves: [{ id: 'm', label: '', points: fnCurve((x) => 85 * (x / 90) ** 2, 0, 90), smooth: true, tone: 'a' }], notes: [note('Accelerating: the line gets steeper', 40, 52)] },
                journey: {
                    curves: [{ id: 'm', label: '', points: [{ x: 0, y: 0 }, { x: 30, y: 60 }, { x: 55, y: 60 }, { x: 85, y: 0 }], tone: 'a' }],
                    notes: [note('Constant speed', 30, 22), note('Stationary', 42.5, 66), note('Returning', 82, 35)],
                },
            }[o.motion];
            return { title: 'Distance-time graph', axes: quadrant('Time', 'Distance'), ...built };
        },
    });

    register({
        id: 'velocity-time',
        subject: 'physics',
        title: 'Velocity-time graph',
        description: 'Velocity against time for constant acceleration or for a journey that speeds up, holds a constant velocity and slows down, with the area under the line (the distance travelled) optionally shaded.',
        options: {
            motion: { values: ['journey', 'constant-acceleration'], default: 'journey', description: 'the shape of the motion' },
            shade: { values: ['none', 'distance'], default: 'none', description: 'shade the area under the line' },
        },
        labels: ['x', 'y'],
        build(o) {
            const pts = o.motion === 'journey'
                ? [{ x: 0, y: 0 }, { x: 25, y: 60 }, { x: 60, y: 60 }, { x: 90, y: 0 }]
                : [{ x: 0, y: 0 }, { x: 85, y: 80 }];
            const shaded = o.shade === 'distance';
            const notes = shaded ? [] : (o.motion === 'journey'
                ? [note('Accelerating', 28, 25), note('Constant velocity', 42.5, 66), note('Decelerating', 91, 34)]
                : [note('Gradient = acceleration', 62, 34)]);
            return {
                title: 'Velocity-time graph',
                axes: quadrant('Time', 'Velocity'),
                curves: [{ id: 'v', label: '', points: pts, tone: 'a' }],
                regions: shaded ? [{ label: 'Area = distance travelled', tone: 'a', vertices: o.motion === 'journey' ? pts : [...pts, { x: 85, y: 0 }], step: 2 }] : [],
                notes,
            };
        },
    });

    register({
        id: 'iv-characteristic',
        subject: 'physics',
        title: 'Current-voltage characteristic',
        description: 'Current against potential difference for a fixed resistor (straight line through the origin), a filament lamp (curve that flattens) or a diode (conducts in one direction only).',
        options: {
            component: { values: ['resistor', 'filament-lamp', 'diode'], default: 'resistor', description: 'which component' },
        },
        labels: ['x', 'y'],
        build(o) {
            const points = {
                resistor: line(-80, -80, 80, 80),
                'filament-lamp': fnCurve((v) => 82 * Math.tanh(v / 42), -92, 92, 40),
                diode: fnCurve((v) => (v < 0 ? 0 : Math.min(96, 0.9 * Math.exp((v - 20) / 9))), -92, 62, 50),
            }[o.component];
            return {
                title: `Current-voltage characteristic of a ${o.component.replace('-', ' ')}`,
                axes: { x: { label: 'V', min: -100, max: 100 }, y: { label: 'I', min: -100, max: 100 } },
                curves: [{ id: 'iv', label: '', points, smooth: o.component !== 'resistor', tone: 'a' }],
            };
        },
    });

    register({
        id: 'radioactive-decay',
        subject: 'physics',
        title: 'Radioactive decay and half-life',
        description: 'The number of undecayed nuclei (or the activity) against time, with one and two half-lives marked.',
        options: {},
        labels: ['x', 'y'],
        build() {
            const half = 22, n = (t) => 90 * 0.5 ** (t / half);
            return {
                title: 'Radioactive decay curve',
                axes: quadrant('Time', 'Undecayed nuclei', { y: { label: 'Undecayed nuclei', marks: [{ at: 90, label: 'N_0' }] } }),
                curves: [{ id: 'N', label: '', points: fnCurve(n, 0, 96, 32), smooth: true, tone: 'a' }],
                points: [
                    { id: 'h1', on: { curve: 'N', x: half }, guides: 'both', xLabel: 't_{½}', yLabel: 'N_0/2', step: 2 },
                    { id: 'h2', on: { curve: 'N', x: half * 2 }, guides: 'both', xLabel: '2t_{½}', yLabel: 'N_0/4', step: 3 },
                ],
            };
        },
    });

    register({
        id: 'force-extension',
        subject: 'physics',
        title: 'Force-extension graph',
        description: 'Force against extension for a spring or wire: a straight line obeying Hooke\'s law up to the limit of proportionality, then curving over.',
        options: {},
        labels: ['x', 'y', 'P'],
        build() {
            return {
                title: 'Force-extension graph',
                axes: quadrant('Extension', 'Force'),
                curves: [{ id: 'F', label: '', points: [{ x: 0, y: 0 }, { x: 25, y: 30 }, { x: 50, y: 60 }, { x: 62, y: 70 }, { x: 76, y: 76 }, { x: 93, y: 79 }], smooth: true, tone: 'a' }],
                points: [{ id: 'P', label: 'P', at: { x: 50, y: 60 }, guides: 'both' }],
                notes: [note('Limit of proportionality', 73, 52), note('Force is proportional to extension', 46, 14)],
            };
        },
    });

    register({
        id: 'terminal-velocity',
        subject: 'physics',
        title: 'Terminal velocity',
        description: 'Velocity against time for a falling object: acceleration falls as drag grows, until the velocity levels off at terminal velocity.',
        options: {},
        labels: ['x', 'y'],
        build() {
            return {
                title: 'Velocity-time graph for a falling object reaching terminal velocity',
                axes: quadrant('Time', 'Velocity', { y: { label: 'Velocity', marks: [{ at: 76, label: 'v_t' }] } }),
                curves: [
                    dashed('vt', line(0, 76, 96, 76)),
                    { id: 'v', label: '', points: fnCurve((t) => 76 * (1 - Math.exp(-t / 17)), 0, 96, 32), smooth: true, tone: 'a' },
                ],
                notes: [note('Terminal velocity: drag = weight', 58, 83), note('Resultant force falls as drag increases', 60, 40)],
            };
        },
    });

    // ---- Chemistry ---------------------------------------------------------

    register({
        id: 'rate-of-reaction',
        subject: 'chemistry',
        title: 'Rate of reaction',
        description: 'Amount of product against time, levelling off when a reactant runs out; optionally compared with a faster reaction (higher temperature, concentration or a catalyst) or with half the amount of limiting reactant.',
        options: {
            compare: { values: ['none', 'faster', 'half-amount'], default: 'none', description: 'a second curve B to compare with the original A' },
        },
        labels: ['x', 'y'],
        build(o) {
            const curves = [{ id: 'A', label: '', points: fnCurve((t) => 70 * (1 - Math.exp(-t / 22)), 0, 96, 32), smooth: true, tone: 'a' }];
            let notes = [note('Reaction finished', 82, 76)];
            if (o.compare === 'faster') {
                curves.push({ id: 'B', label: '', points: fnCurve((t) => 70 * (1 - Math.exp(-t / 9)), 0, 96, 40), smooth: true, tone: 'b', step: 2 });
                notes = [note('A', 34, 44), note('B: faster, same final amount', 30, 78, 2)];
            } else if (o.compare === 'half-amount') {
                curves.push({ id: 'B', label: '', points: fnCurve((t) => 35 * (1 - Math.exp(-t / 22)), 0, 96, 32), smooth: true, tone: 'b', step: 2 });
                notes = [note('A', 60, 72), note('B: half the limiting reactant', 62, 26, 2)];
            }
            return { title: 'Rate of reaction graph', axes: quadrant('Time', 'Amount of product'), curves, notes };
        },
    });

    register({
        id: 'reaction-profile',
        subject: 'chemistry',
        title: 'Reaction profile',
        description: 'Energy against progress of reaction for an exothermic or endothermic reaction, with the activation energy and overall energy change marked, optionally with the lower-energy route a catalyst provides.',
        options: {
            type: { values: ['exothermic', 'endothermic'], default: 'exothermic', description: 'whether the products end lower or higher than the reactants' },
            catalyst: { values: ['no', 'yes'], default: 'no', description: 'add the catalysed route' },
        },
        labels: ['x', 'y'],
        build(o) {
            const exo = o.type === 'exothermic';
            const R = exo ? 54 : 22, Pd = exo ? 22 : 54, peak = 90, low = 70;
            // Level, up over a hump, level again: half a cosine each side of the peak.
            const profile = (top) => (x) => {
                if (x <= 25) return R;
                if (x <= 50) return R + (top - R) * (1 - Math.cos(Math.PI * (x - 25) / 25)) / 2;
                if (x <= 75) return Pd + (top - Pd) * (1 + Math.cos(Math.PI * (x - 50) / 25)) / 2;
                return Pd;
            };
            const cat = o.catalyst === 'yes';
            const curves = [
                dashed('level', line(25, R, 88, R)),
                { id: 'path', label: '', points: fnCurve(profile(peak), 4, 96, 46), tone: 'a' },
            ];
            const points = [{ id: 'top', on: { curve: 'path', x: 50 }, dot: false }];
            const arrows = [
                { from: { x: 50, y: R }, to: 'top', label: cat ? '' : 'E_a', step: 2 },
                { from: { x: 80, y: R }, to: { x: 80, y: Pd }, label: exo ? 'Energy released' : 'Energy absorbed', step: 3 },
            ];
            const notes = [note('Reactants', 13, R + 5), note('Products', 90, exo ? Pd - 7 : Pd + 5)];
            if (cat) {
                curves.push({ id: 'cat', label: '', points: fnCurve(profile(low), 4, 96, 46), tone: 'b', style: 'dashed', step: 4 });
                // Named in the empty corner rather than with a second arrow: two
                // arrows under one hump cannot both be labelled legibly.
                notes.push(note('Dashed: with a catalyst,', 80, 90, 4), note('lower activation energy', 80, 83, 4));
            }
            return {
                title: `Reaction profile for an ${o.type} reaction`,
                axes: quadrant('Progress of reaction', 'Energy'),
                curves, points, arrows, notes,
            };
        },
    });

    register({
        id: 'heating-curve',
        subject: 'chemistry',
        title: 'Heating curve',
        description: 'Temperature against time as a solid is heated through melting and boiling: the temperature stays constant during each change of state.',
        options: {},
        labels: ['x', 'y'],
        build() {
            return {
                title: 'Heating curve showing changes of state',
                axes: quadrant('Time', 'Temperature', { y: { label: 'Temperature', marks: [{ at: 35, label: 'm.p.' }, { at: 72, label: 'b.p.' }] } }),
                curves: [{ id: 'T', label: '', points: [{ x: 0, y: 10 }, { x: 18, y: 35 }, { x: 38, y: 35 }, { x: 60, y: 72 }, { x: 82, y: 72 }, { x: 96, y: 90 }], tone: 'a' }],
                points: [
                    { id: 'm', at: { x: 18, y: 35 }, guides: 'y', dot: false },
                    { id: 'b', at: { x: 60, y: 72 }, guides: 'y', dot: false },
                ],
                notes: [note('Solid', 6, 30), note('Melting', 28, 41), note('Liquid', 42, 60), note('Boiling', 71, 78), note('Gas', 86, 90)],
            };
        },
    });

    register({
        id: 'equilibrium-concentration',
        subject: 'chemistry',
        title: 'Reaching equilibrium',
        description: 'Concentrations of reactants and products against time for a reversible reaction, levelling off once dynamic equilibrium is reached.',
        options: {},
        labels: ['x', 'y', 'reactants', 'products'],
        build() {
            return {
                title: 'Concentration against time for a reversible reaction reaching equilibrium',
                axes: quadrant('Time', 'Concentration'),
                curves: [
                    dashed('eq', line(60, 0, 60, 92)),
                    { id: 'reactants', label: 'Reactants', points: fnCurve((t) => 28 + 58 * Math.exp(-t / 15), 0, 94, 32), smooth: true, tone: 'a' },
                    { id: 'products', label: 'Products', points: fnCurve((t) => 54 * (1 - Math.exp(-t / 15)), 0, 94, 32), smooth: true, tone: 'b' },
                ],
                notes: [note('Equilibrium reached', 78, 88), note('Concentrations constant', 78, 80)],
            };
        },
    });

    register({
        id: 'maxwell-boltzmann',
        subject: 'chemistry',
        title: 'Maxwell-Boltzmann distribution',
        description: 'The distribution of molecular energies with the activation energy marked and the particles able to react shaded, optionally compared with a higher temperature.',
        options: {
            temperature: { values: ['single', 'compare'], default: 'single', description: 'one temperature, or a higher one as well' },
        },
        labels: ['x', 'y'],
        build(o) {
            const Ea = 62;
            const dist = (b, k) => (e) => k * (e / b) * Math.exp(1 - e / b);
            const t1 = dist(22, 78), t2 = dist(32, 78 * 22 / 32);
            // The area to the right of Ea, under a curve.
            const tail = (fn) => [{ x: Ea, y: 0 }, ...fnCurve(fn, Ea, 98, 12), { x: 98, y: 0 }];
            const both = o.temperature === 'compare';
            const curves = [
                { id: 'Ea', label: 'E_a', points: line(Ea, 0, Ea, 70), style: 'dashed', tone: 'ink' },
                { id: 'T1', label: '', points: fnCurve(t1, 0, 98, 40), smooth: true, tone: 'a' },
            ];
            const regions = [{ label: '', tone: 'a', vertices: tail(t1), step: 2 }];
            const notes = [note(both ? 'T_1' : 'Most probable energy', both ? 22 : 30, 84)];
            if (both) {
                curves.push({ id: 'T2', label: '', points: fnCurve(t2, 0, 98, 40), smooth: true, tone: 'b', step: 3 });
                regions.push({ label: '', tone: 'b', vertices: tail(t2), step: 3 });
                notes.push(note('T_2 (higher)', 44, 61, 3));
            }
            notes.push(note('Enough energy to react', 82, 44, 2));
            return { title: 'Maxwell-Boltzmann distribution of molecular energies', axes: quadrant('Energy', 'Number of particles'), curves, regions, notes };
        },
    });

    // ---- Biology -----------------------------------------------------------

    register({
        id: 'enzyme-activity',
        subject: 'biology',
        title: 'Enzyme activity',
        description: 'Rate of an enzyme-controlled reaction against temperature (rising to an optimum, then falling as the enzyme denatures), pH (a peak at the optimum) or substrate concentration (levelling off when the enzymes are saturated).',
        options: {
            factor: { values: ['temperature', 'ph', 'substrate-concentration'], default: 'temperature', description: 'what is on the x axis' },
        },
        labels: ['x', 'y'],
        build(o) {
            const y = 'Rate of reaction';
            if (o.factor === 'temperature') {
                return {
                    title: 'Effect of temperature on enzyme activity',
                    axes: quadrant('Temperature', y),
                    curves: [{ id: 'r', label: '', points: [...fnCurve((t) => 6 * Math.exp(t / 22.4), 4, 58, 9), { x: 62, y: 76 }, { x: 67, y: 56 }, { x: 73, y: 24 }, { x: 79, y: 4 }], smooth: true, tone: 'a' }],
                    points: [{ id: 'opt', at: { x: 58, y: 80 }, guides: 'x', xLabel: 'Optimum' }],
                    notes: [note('More frequent collisions', 22, 44), note('Enzyme denatures', 86, 44)],
                };
            }
            if (o.factor === 'ph') {
                return {
                    title: 'Effect of pH on enzyme activity',
                    axes: quadrant('pH', y),
                    curves: [{ id: 'r', label: '', points: fnCurve((x) => 80 * Math.exp(-(((x - 50) / 16) ** 2)), 8, 92, 36), smooth: true, tone: 'a' }],
                    points: [{ id: 'opt', at: { x: 50, y: 80 }, guides: 'x', xLabel: 'Optimum pH' }],
                };
            }
            return {
                title: 'Effect of substrate concentration on enzyme activity',
                axes: quadrant('Substrate concentration', y, { y: { label: y, marks: [{ at: 80, label: 'V_{max}' }] } }),
                curves: [
                    dashed('vmax', line(0, 80, 96, 80)),
                    { id: 'r', label: '', points: fnCurve((x) => 80 * x / (x + 12), 0, 96, 36), smooth: true, tone: 'a' },
                ],
                notes: [note('All active sites occupied', 70, 62)],
            };
        },
    });

    register({
        id: 'population-growth',
        subject: 'biology',
        title: 'Population growth curve',
        description: 'Population size against time: a sigmoid curve with lag, exponential and stationary phases levelling at the carrying capacity, or unchecked exponential growth.',
        options: {
            type: { values: ['sigmoid', 'exponential'], default: 'sigmoid', description: 'limited by resources, or unlimited' },
        },
        labels: ['x', 'y'],
        build(o) {
            if (o.type === 'exponential') {
                return {
                    title: 'Exponential population growth',
                    axes: quadrant('Time', 'Population size'),
                    curves: [{ id: 'N', label: '', points: fnCurve((t) => 3 * Math.exp(t / 27), 0, 90, 32), smooth: true, tone: 'a' }],
                };
            }
            return {
                title: 'Sigmoid population growth curve',
                axes: quadrant('Time', 'Population size', { y: { label: 'Population size', marks: [{ at: 80, label: 'K' }] } }),
                curves: [
                    dashed('K', line(0, 80, 96, 80)),
                    { id: 'N', label: '', points: fnCurve((t) => 80 / (1 + Math.exp(-(t - 45) / 9)), 0, 96, 40), smooth: true, tone: 'a' },
                ],
                notes: [note('Lag phase', 13, 12), note('Exponential phase', 24, 46), note('Stationary phase', 80, 70), note('Carrying capacity', 30, 86)],
            };
        },
    });

    register({
        id: 'predator-prey',
        subject: 'biology',
        title: 'Predator-prey cycles',
        description: 'Predator and prey population sizes against time: both cycle, with the predator peaks following the prey peaks.',
        options: {},
        labels: ['x', 'y', 'prey', 'predator'],
        build() {
            const wave = (mid, amp, lag) => (t) => mid + amp * Math.sin(2 * Math.PI * (t - lag) / 40);
            return {
                title: 'Predator-prey population cycles',
                axes: quadrant('Time', 'Population size'),
                curves: [
                    { id: 'prey', label: 'Prey', points: fnCurve(wave(55, 28, 0), 0, 94, 50), smooth: true, tone: 'a' },
                    { id: 'predator', label: 'Predator', points: fnCurve(wave(30, 16, 10), 0, 94, 50), smooth: true, tone: 'b' },
                ],
            };
        },
    });

    register({
        id: 'limiting-factors',
        subject: 'biology',
        title: 'Limiting factors in photosynthesis',
        description: 'Rate of photosynthesis against light intensity, rising then levelling off when another factor becomes limiting; optionally with a second curve at a higher carbon dioxide concentration (or temperature).',
        options: {
            compare: { values: ['none', 'second-condition'], default: 'none', description: 'add a second curve where the other factor is raised' },
        },
        labels: ['x', 'y', 'low', 'high'],
        build(o) {
            const two = o.compare === 'second-condition';
            const curves = [
                dashed('split', line(34, 0, 34, 96)),
                { id: 'low', label: two ? 'Low CO_2' : '', points: fnCurve((x) => 46 * (1 - Math.exp(-x / 12)), 0, 94, 36), smooth: true, tone: 'a' },
            ];
            if (two) curves.push({ id: 'high', label: 'High CO_2', points: fnCurve((x) => 76 * (1 - Math.exp(-x / 17)), 0, 94, 36), smooth: true, tone: 'b', step: 2 });
            return {
                title: 'Limiting factors in photosynthesis',
                axes: quadrant('Light intensity', 'Rate of photosynthesis'),
                curves,
                notes: [note('Light is limiting', 16, 92), note('Another factor is limiting', 66, 92)],
            };
        },
    });

    register({
        id: 'oxygen-dissociation',
        subject: 'biology',
        title: 'Oxygen dissociation curve',
        description: 'Percentage saturation of haemoglobin against the partial pressure of oxygen (a sigmoid curve), optionally with the Bohr shift to the right at higher carbon dioxide concentration.',
        options: {
            shift: { values: ['none', 'bohr'], default: 'none', description: 'add the curve shifted right by more carbon dioxide' },
        },
        labels: ['x', 'y'],
        build(o) {
            const hill = (p50) => (p) => 95 * p ** 2.8 / (p ** 2.8 + p50 ** 2.8);
            const curves = [{ id: 'c1', label: '', points: fnCurve(hill(26), 0, 96, 40), smooth: true, tone: 'a' }];
            const notes = [], arrows = [];
            if (o.shift === 'bohr') {
                curves.push({ id: 'c2', label: '', points: fnCurve(hill(38), 0, 96, 40), smooth: true, tone: 'b', step: 2 });
                arrows.push({ from: 'a1', to: 'a2', step: 2 });
                notes.push(note('Low CO_2', 15, 62), note('High CO_2', 62, 50, 2));
            } else {
                notes.push(note('Loads oxygen in the lungs', 72, 84), note('Unloads in respiring tissues', 44, 18));
            }
            const points = o.shift === 'bohr'
                ? [{ id: 'a1', on: { curve: 'c1', y: 47 }, dot: false, step: 2 }, { id: 'a2', on: { curve: 'c2', y: 47 }, dot: false, step: 2 }]
                : [];
            return { title: 'Oxygen dissociation curve for haemoglobin', axes: quadrant('Partial pressure of oxygen', 'Saturation of haemoglobin (%)'), curves, points, notes, arrows };
        },
    });

    // ---- Maths -------------------------------------------------------------

    const xy = (xMin, xMax, yMin, yMax) => ({ origin: 'O', x: { label: 'x', min: xMin, max: xMax }, y: { label: 'y', min: yMin, max: yMax } });
    const mark = (id, x, y, extra) => ({ id, at: { x, y }, ...extra });

    register({
        id: 'quadratic-sketch',
        subject: 'maths',
        title: 'Sketch of a quadratic',
        description: 'A parabola with its roots, y-intercept and turning point marked: y = x² − 2x − 3, or its reflection.',
        options: {
            orientation: { values: ['positive', 'negative'], default: 'positive', description: 'positive = U-shaped, negative = ∩-shaped' },
        },
        labels: ['x', 'y', 'curve', 'vertex'],
        build(o) {
            const s = o.orientation === 'positive' ? 1 : -1;
            const f = (x) => s * (x * x - 2 * x - 3);
            return {
                title: 'Sketch of a quadratic curve',
                axes: s > 0 ? xy(-4, 6, -6, 10) : xy(-4, 6, -10, 6),
                curves: [{ id: 'curve', label: s > 0 ? 'y = x² − 2x − 3' : 'y = −x² + 2x + 3', points: fnCurve(f, -3, 5, 40), smooth: true, tone: 'a' }],
                points: [
                    mark('r1', -1, 0, { xLabel: '−1' }),
                    mark('r2', 3, 0, { xLabel: '3' }),
                    mark('c', 0, f(0), { yLabel: s > 0 ? '−3' : '3' }),
                    mark('vertex', 1, f(1), { label: s > 0 ? '(1, −4)' : '(1, 4)', step: 2 }),
                ],
            };
        },
    });

    register({
        id: 'cubic-sketch',
        subject: 'maths',
        title: 'Sketch of a cubic',
        description: 'A cubic with three real roots and its y-intercept marked: y = (x + 2)(x − 1)(x − 3), or its reflection.',
        options: {
            leading: { values: ['positive', 'negative'], default: 'positive', description: 'the sign of the x³ term' },
        },
        labels: ['x', 'y', 'curve'],
        build(o) {
            const s = o.leading === 'positive' ? 1 : -1;
            const f = (x) => s * (x + 2) * (x - 1) * (x - 3);
            return {
                title: 'Sketch of a cubic curve',
                axes: xy(-4, 5, -12, 12),
                curves: [{ id: 'curve', label: s > 0 ? 'y = (x + 2)(x − 1)(x − 3)' : 'y = −(x + 2)(x − 1)(x − 3)', points: fnCurve(f, -2.7, 3.7, 44), smooth: true, tone: 'a' }],
                points: [
                    mark('r1', -2, 0, { xLabel: '−2' }), mark('r2', 1, 0, { xLabel: '1' }), mark('r3', 3, 0, { xLabel: '3' }),
                    mark('c', 0, f(0), { yLabel: s > 0 ? '6' : '−6' }),
                ],
            };
        },
    });

    register({
        id: 'reciprocal-sketch',
        subject: 'maths',
        title: 'Sketch of a reciprocal',
        description: 'y = k/x: two branches with the axes as asymptotes, in opposite quadrants.',
        options: {
            sign: { values: ['positive', 'negative'], default: 'positive', description: 'the sign of k' },
        },
        labels: ['x', 'y'],
        build(o) {
            const k = o.sign === 'positive' ? 2 : -2;
            const f = (x) => k / x;
            return {
                title: 'Sketch of a reciprocal curve',
                axes: xy(-5, 5, -5, 5),
                curves: [
                    { id: 'right', label: '', points: fnCurve(f, 0.42, 4.8, 40), smooth: true, tone: 'a' },
                    { id: 'left', label: '', points: fnCurve(f, -4.8, -0.42, 40), smooth: true, tone: 'a' },
                ],
                // Beside the curve: both branches end hard against an axis.
                notes: [note(o.sign === 'positive' ? 'y = k/x, k > 0' : 'y = k/x, k < 0', 3, o.sign === 'positive' ? 2.4 : -2.4)],
            };
        },
    });

    register({
        id: 'exponential-sketch',
        subject: 'maths',
        title: 'Sketch of an exponential',
        description: 'y = aˣ for growth (a > 1) or decay (0 < a < 1), crossing the y axis at 1 with the x axis as an asymptote.',
        options: {
            type: { values: ['growth', 'decay'], default: 'growth', description: 'growth or decay' },
        },
        labels: ['x', 'y'],
        build(o) {
            const grow = o.type === 'growth';
            return {
                title: `Sketch of exponential ${o.type}`,
                axes: xy(-4, 4, -1.5, 9),
                curves: [{ id: 'curve', label: '', points: fnCurve((x) => 2 ** (grow ? x : -x), -4, 4, 40), smooth: true, tone: 'a' }],
                points: [mark('c', 0, 1, { yLabel: '1' })],
                notes: [note(grow ? 'y = aˣ, a > 1' : 'y = aˣ, 0 < a < 1', grow ? 1.3 : -1.3, 7.6)],
            };
        },
    });

    register({
        id: 'simultaneous-graphical',
        subject: 'maths',
        title: 'Solving simultaneous equations graphically',
        description: 'Two graphs with their points of intersection found and read off: a line and a quadratic (two solutions) or two lines (one solution).',
        options: {
            type: { values: ['line-and-curve', 'two-lines'], default: 'line-and-curve', description: 'which pair of graphs' },
        },
        labels: ['x', 'y', 'f', 'g'],
        build(o) {
            if (o.type === 'two-lines') {
                return {
                    title: 'Two straight lines and their point of intersection',
                    axes: xy(-2, 6, -3, 8),
                    curves: [
                        { id: 'f', label: 'y = 2x − 1', points: line(-1, -3, 4.4, 7.8), tone: 'a' },
                        { id: 'g', label: 'y = 5 − x', points: line(-2, 7, 6, -1), tone: 'b' },
                    ],
                    points: [{ id: 'A', intersect: ['f', 'g'], guides: 'both', xLabel: '2', yLabel: '3', label: '(2, 3)' }],
                };
            }
            return {
                title: 'A line and a quadratic and their points of intersection',
                axes: xy(-3, 6, -3, 10),
                curves: [
                    { id: 'f', label: 'y = x² − 2x − 1', points: fnCurve((x) => x * x - 2 * x - 1, -2.4, 4.5, 40), smooth: true, tone: 'a' },
                    { id: 'g', label: 'y = x + 3', points: line(-3, 0, 6, 9), tone: 'b' },
                ],
                points: [
                    { id: 'A', intersect: ['f', 'g'], guides: 'both', xLabel: '−1', yLabel: '2', label: 'A' },
                    { id: 'B', intersect: ['f', 'g'], pick: 'last', guides: 'both', xLabel: '4', yLabel: '7', label: 'B', step: 2 },
                ],
            };
        },
    });

    // ---- Geography and business --------------------------------------------

    register({
        id: 'demographic-transition',
        subject: 'geography',
        title: 'Demographic transition model',
        description: 'Birth rate, death rate and total population across the five stages of the demographic transition model.',
        options: {},
        labels: ['x', 'y', 'birth', 'death', 'total'],
        build() {
            const through = (ys) => ys.map((y, i) => ({ x: i * 20, y }));
            return {
                title: 'Demographic transition model',
                axes: quadrant('Time', 'Rate per 1000'),
                curves: [
                    ...[20, 40, 60, 80].map((x, i) => dashed(`s${i + 1}`, line(x, 0, x, 88))),
                    { id: 'birth', label: 'Birth rate', points: through([70, 70, 68, 38, 24, 18]), smooth: true, tone: 'a' },
                    { id: 'death', label: 'Death rate', points: through([66, 64, 34, 22, 20, 24]), smooth: true, tone: 'b' },
                    { id: 'total', label: 'Population', points: through([8, 10, 26, 56, 76, 80]), smooth: true, tone: 'c', step: 2 },
                ],
                notes: [1, 2, 3, 4, 5].map((n) => note(`Stage ${n}`, n * 20 - 10, 95)),
            };
        },
    });

    register({
        id: 'break-even',
        subject: 'business',
        title: 'Break-even chart',
        description: 'Fixed costs, total costs and total revenue against output, with the break-even point and the areas of loss and profit.',
        options: {
            shade: { values: ['profit-and-loss', 'none'], default: 'profit-and-loss', description: 'shade the loss and profit areas' },
        },
        labels: ['x', 'y', 'FC', 'TC', 'TR', 'BE'],
        build(o) {
            return {
                title: 'Break-even chart',
                axes: quadrant('Output', 'Costs and revenue (£)'),
                curves: [
                    { id: 'FC', label: 'Fixed costs', points: line(0, 25, 92, 25), tone: 'c' },
                    { id: 'TC', label: 'Total costs', points: line(0, 25, 92, 25 + 0.6 * 92), tone: 'b' },
                    { id: 'TR', label: 'Total revenue', points: line(0, 0, 92, 92), tone: 'a', step: 2 },
                ],
                points: [{ id: 'BE', intersect: ['TC', 'TR'], guides: 'both', xLabel: 'Break-even output', step: 3 }],
                regions: o.shade === 'none' ? [] : [
                    { label: 'Loss', tone: 'b', vertices: [{ x: 0, y: 0 }, { x: 0, y: 25 }, 'BE'], step: 3 },
                    { label: 'Profit', tone: 'c', vertices: ['BE', { x: 92, y: 25 + 0.6 * 92 }, { x: 92, y: 92 }], step: 3 },
                ],
            };
        },
    });

    register({
        id: 'product-life-cycle',
        subject: 'business',
        title: 'Product life cycle',
        description: 'Sales against time through introduction, growth, maturity and decline, optionally with an extension strategy lifting sales again.',
        options: {
            extension: { values: ['no', 'yes'], default: 'no', description: 'add the effect of an extension strategy' },
        },
        labels: ['x', 'y'],
        build(o) {
            const curves = [
                ...[19, 45, 74].map((x, i) => dashed(`s${i + 1}`, line(x, 0, x, 88))),
                { id: 'sales', label: '', points: [{ x: 0, y: 2 }, { x: 14, y: 8 }, { x: 30, y: 30 }, { x: 45, y: 60 }, { x: 60, y: 76 }, { x: 72, y: 76 }, { x: 85, y: 58 }, { x: 97, y: 34 }], smooth: true, tone: 'a' },
            ];
            const notes = [note('Introduction', 9.5, 94), note('Growth', 32, 94), note('Maturity', 59.5, 94), note('Decline', 87, 94)];
            if (o.extension === 'yes') {
                curves.push({ id: 'ext', label: '', points: [{ x: 72, y: 76 }, { x: 80, y: 80 }, { x: 89, y: 87 }, { x: 97, y: 90 }], smooth: true, tone: 'b', style: 'dashed', step: 2 });
                notes.push(note('Extension strategy', 84, 70, 2));
            }
            return { title: 'Product life cycle', axes: quadrant('Time', 'Sales'), curves, notes };
        },
    });

    // ---- Resolving ---------------------------------------------------------

    const list = (subject) => [...REGISTRY.values()].filter(p => !subject || p.subject === subject);
    const get = (id) => REGISTRY.get(id) || null;

    /** A preset's options with anything missing or not on its list replaced by the default. */
    function cleanOptions(preset, raw) {
        const given = raw && typeof raw === 'object' ? raw : {};
        const out = {};
        for (const [name, def] of Object.entries(preset.options)) {
            out[name] = def.values.includes(given[name]) ? given[name] : def.default;
        }
        return out;
    }

    /** Replace named labels: 'x' and 'y' are the axis titles, anything else a curve, point or region id. */
    function relabel(spec, labels) {
        if (!labels || typeof labels !== 'object') return spec;
        for (const [id, text] of Object.entries(labels)) {
            if (typeof text !== 'string' || !text.trim()) continue;
            const t = text.trim();
            if (id === 'x' || id === 'y') { spec.axes[id].label = t; continue; }
            for (const kind of ['curves', 'points', 'regions', 'arrows']) {
                for (const el of (spec[kind] || [])) if (el.id === id) el.label = t;
            }
        }
        return spec;
    }

    /**
     * `{ preset, options, labels }` or `{ preset: 'custom', custom: spec }` to a
     * normalised curve-diagram spec. Null when there is nothing drawable, or
     * when the result names a crossing its curves don't have – better no
     * diagram than one with an equilibrium floating in space.
     */
    function resolve(raw) {
        if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return null; } }
        if (!raw || typeof raw !== 'object') return null;
        const preset = get(raw.preset);
        const options = preset ? cleanOptions(preset, raw.options) : null;
        const built = preset
            ? { ...relabel(preset.build(options), raw.labels), source: { preset: preset.id, options } }
            : (raw.custom && typeof raw.custom === 'object' ? raw.custom : (raw.curves ? raw : null));
        const spec = CurveDiagram.normalize(built);
        if (!spec) return null;
        // A custom diagram with one bad reference still draws; a preset with one is a bug in the preset.
        if (preset && CurveDiagram.unresolved(spec).length) return null;
        if (typeof raw.title === 'string' && raw.title.trim()) spec.title = raw.title.trim().slice(0, 120);
        return spec;
    }

    /**
     * The tool-schema shape for asking a model for one of these: pick a preset
     * and its options, or write a custom spec. `subject` narrows the presets
     * offered; the custom route is always there.
     */
    function toolSchema({ subject, description } = {}) {
        const presets = list(subject);
        const options = {};
        for (const p of presets) {
            for (const [name, def] of Object.entries(p.options)) {
                if (!options[name]) options[name] = { type: 'string', enum: [], description: '' };
                for (const v of def.values) if (!options[name].enum.includes(v)) options[name].enum.push(v);
            }
        }
        const catalogue = presets.map(p => {
            const opts = Object.entries(p.options).map(([n, d]) => `${n}: ${d.values.join(' | ')} (${d.description})`).join('; ');
            return `"${p.id}" – ${p.description}${opts ? ` Options – ${opts}.` : ''} Relabel ids: ${p.labels.join(', ')}.`;
        }).join('\n');
        const ref = { description: 'A point id, or { x | xOf, y | yOf } where xOf/yOf borrow that coordinate from a named point - e.g. { "x": 0, "yOf": "E" } is the point on the y axis level with E.' };
        return {
            type: 'object',
            required: ['preset'],
            description: `${description || 'The diagram to draw.'} Choose a preset when one fits - its curves are already placed so they cross correctly - and only use "custom" for a diagram no preset covers.\nPresets:\n${catalogue}`,
            properties: {
                preset: { type: 'string', enum: [...presets.map(p => p.id), 'custom'] },
                options: { type: 'object', description: 'Options for the chosen preset; omit any you want left at its default.', properties: options },
                labels: { type: 'object', description: 'Optional replacement labels keyed by relabel id - "x" and "y" are the axis titles. Write subscripts as P_1 or Q_e.', additionalProperties: { type: 'string' } },
                custom: {
                    type: 'object',
                    description: 'Only when preset is "custom". Coordinates run 0-100 on both axes unless the axis sets its own min/max. Never place a crossing by eye: define it as a point with "intersect" and the renderer finds it.',
                    properties: {
                        axes: {
                            type: 'object',
                            properties: {
                                origin: { type: 'string', description: 'Label at the origin, e.g. "O", or omit.' },
                                x: { type: 'object', properties: { label: { type: 'string' }, min: { type: 'number' }, max: { type: 'number' }, numeric: { type: 'boolean', description: 'true to print a numbered scale' } } },
                                y: { type: 'object', properties: { label: { type: 'string' }, min: { type: 'number' }, max: { type: 'number' }, numeric: { type: 'boolean' } } },
                            },
                        },
                        curves: {
                            type: 'array', minItems: 1, maxItems: CurveDiagram.LIMITS.curves,
                            items: {
                                type: 'object', required: ['id', 'points'],
                                properties: {
                                    id: { type: 'string' }, label: { type: 'string' },
                                    points: { type: 'array', minItems: 2, maxItems: 12, items: { type: 'object', required: ['x', 'y'], properties: { x: { type: 'number' }, y: { type: 'number' } } }, description: 'Two points for a straight line; more, with smooth=true, for a curve through them in order.' },
                                    smooth: { type: 'boolean' },
                                    style: { type: 'string', enum: ['solid', 'dashed'] },
                                    tone: { type: 'string', enum: CurveDiagram.TONES, description: 'Colour family - give a curve and its shifted twin the same tone.' },
                                    step: { type: 'integer', minimum: 1, maximum: CurveDiagram.LIMITS.steps, description: 'Build order: 1 = the starting diagram, 2 = what changes, 3 = the result.' },
                                },
                            },
                        },
                        points: {
                            type: 'array', maxItems: CurveDiagram.LIMITS.points,
                            items: {
                                type: 'object', required: ['id'],
                                description: 'Give exactly one of intersect, on or at.',
                                properties: {
                                    id: { type: 'string' }, label: { type: 'string' },
                                    intersect: { type: 'array', minItems: 2, maxItems: 2, items: { type: 'string' }, description: 'Two curve ids - the point is where they cross.' },
                                    on: { type: 'object', description: 'A point on a curve: { curve, x } or { curve, y }, or xOf / yOf to line it up with another point.', properties: { curve: { type: 'string' }, x: { type: 'number' }, y: { type: 'number' }, xOf: { type: 'string' }, yOf: { type: 'string' } } },
                                    at: ref,
                                    pick: { type: 'string', enum: ['first', 'last'], description: 'Which crossing, left to right, when there is more than one.' },
                                    guides: { type: 'string', enum: ['both', 'x', 'y', 'none'], description: 'Dashed lines from the point to the axes.' },
                                    xLabel: { type: 'string', description: 'Label on the x axis under the point, e.g. "Q_1".' },
                                    yLabel: { type: 'string', description: 'Label on the y axis level with the point, e.g. "P_1".' },
                                    step: { type: 'integer', minimum: 1, maximum: CurveDiagram.LIMITS.steps },
                                },
                            },
                        },
                        regions: {
                            type: 'array', maxItems: CurveDiagram.LIMITS.regions,
                            items: {
                                type: 'object', required: ['vertices'],
                                properties: { label: { type: 'string' }, vertices: { type: 'array', minItems: 3, maxItems: CurveDiagram.LIMITS.regionVertices, items: ref }, tone: { type: 'string', enum: CurveDiagram.TONES }, step: { type: 'integer', minimum: 1, maximum: CurveDiagram.LIMITS.steps } },
                            },
                        },
                        arrows: {
                            type: 'array', maxItems: CurveDiagram.LIMITS.arrows,
                            items: {
                                type: 'object', required: ['from', 'to'],
                                properties: { from: { description: 'A curve id (for a shift arrow between two curves), a point id, or { x, y }.' }, to: {}, label: { type: 'string' }, heads: { type: 'string', enum: ['end', 'both'] }, step: { type: 'integer', minimum: 1, maximum: CurveDiagram.LIMITS.steps } },
                            },
                        },
                    },
                },
            },
        };
    }

    return { register, list, get, resolve, toolSchema };
}));
